import React from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  Package,
  RefreshCw,
  Shield,
} from "lucide-react";
import { adminApi } from "../../lib/api";

export default function DistributionDriftPanel() {
  const [readiness, setReadiness] = React.useState(null);
  const [deferred, setDeferred] = React.useState([]);
  const [audit, setAudit] = React.useState([]);
  const [deferredRuns, setDeferredRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [readyRes, defRes, auditRes, runsRes] = await Promise.all([
        adminApi().get("/admin/distribution/pilot/readiness", { params: { hub_id: "roskilde" } }),
        adminApi().get("/admin/distribution/deferred-queue", { params: { hub_id: "roskilde" } }),
        adminApi().get("/admin/distribution/hub-audit", { params: { hub_id: "roskilde", limit: 20 } }),
        adminApi().get("/admin/distribution/deferred-runs", { params: { limit: 10 } }),
      ]);
      setReadiness(readyRes.data);
      setDeferred(defRes.data.items || []);
      setAudit(auditRes.data.items || []);
      setDeferredRuns(runsRes.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente drift-data");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-brand-text2 py-8" data-testid="dist-drift-loading">
        <Loader2 className="animate-spin" size={18} /> Indlæser Roskilde drift...
      </div>
    );
  }

  const counts = readiness?.counts || {};
  const checks = readiness?.checks || [];

  return (
    <div className="space-y-5" data-testid="distribution-drift-panel">
      <div className="bg-white border border-brand p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Shield size={20} className="text-brand-red" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-semibold">
              Pilot · Roskilde
            </div>
            <div className="font-display text-lg text-brand-dark">
              Go-live tjekliste
              {readiness?.ok ? (
                <span className="ml-2 text-sm text-emerald-600 font-sans">— software klar</span>
              ) : (
                <span className="ml-2 text-sm text-amber-600 font-sans">— se blockers</span>
              )}
            </div>
          </div>
          <button type="button" onClick={load} className="px-3 py-1.5 text-xs border border-brand flex items-center gap-1">
            <RefreshCw size={12} /> Opdater
          </button>
        </div>
        <ul className="mt-4 space-y-2">
          {checks.map((c) => (
            <li key={c.id} className="flex items-start gap-2 text-sm">
              {c.ok ? (
                <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
              ) : (
                <Circle size={16} className="text-amber-500 shrink-0 mt-0.5" />
              )}
              <span>
                <strong>{c.label}</strong>
                {c.detail ? <span className="text-brand-text2"> — {c.detail}</span> : null}
              </span>
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 text-sm">
          <div className="bg-brand-bg p-3 border border-brand">
            <div className="text-xs text-brand-text2">På HUB</div>
            <div className="font-display text-xl">{counts.at_hub ?? 0}</div>
          </div>
          <div className="bg-brand-bg p-3 border border-brand">
            <div className="text-xs text-brand-text2">Deferred (i morgen)</div>
            <div className="font-display text-xl">{counts.deferred_next_day ?? 0}</div>
          </div>
          <div className="bg-brand-bg p-3 border border-brand">
            <div className="text-xs text-brand-text2">Afventer linehaul</div>
            <div className="font-display text-xl">{counts.awaiting_linehaul ?? 0}</div>
          </div>
          <div className="bg-brand-bg p-3 border border-brand">
            <div className="text-xs text-brand-text2">Oprettet, ikke inbound</div>
            <div className="font-display text-xl">{counts.created_not_inbound ?? 0}</div>
          </div>
        </div>
        {readiness?.hub_station && (
          <p className="text-xs text-brand-text2 mt-4 leading-relaxed">
            HUB Station på lager-PC: API <code>{readiness.hub_station.api_url}</code> · HUB{" "}
            <code>{readiness.hub_station.hub_id}</code> · station{" "}
            <code>{readiness.hub_station.station_id}</code> · version{" "}
            <code>{readiness.hub_station.recommended_version}</code>
          </p>
        )}
      </div>

      <section className="bg-white border border-brand overflow-hidden">
        <div className="p-4 border-b border-brand flex items-center gap-2">
          <Clock size={16} className="text-brand-red" />
          <h3 className="font-semibold text-brand-dark">Deferred kø (Roskilde)</h3>
        </div>
        {deferred.length === 0 ? (
          <div className="p-6 text-sm text-brand-text2 text-center">Ingen deferred pakker lige nu.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-brand-bg text-xs uppercase text-brand-text2">
              <tr>
                <th className="text-left p-3">Label</th>
                <th className="text-left p-3">Modtager</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {deferred.map((p) => (
                <tr key={p.id} className="border-t border-brand">
                  <td className="p-3 font-mono text-xs">{p.shipment_number || p.parcel_number}</td>
                  <td className="p-3">
                    {p.recipient_name}
                    <div className="text-xs text-brand-text2">
                      {p.delivery_postal_code} {p.delivery_city}
                    </div>
                  </td>
                  <td className="p-3">{p.distribution_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="bg-white border border-brand overflow-hidden">
        <div className="p-4 border-b border-brand flex items-center gap-2">
          <Package size={16} className="text-brand-red" />
          <h3 className="font-semibold text-brand-dark">HUB audit (Roskilde)</h3>
        </div>
        {audit.length === 0 ? (
          <div className="p-6 text-sm text-brand-text2 text-center">Ingen audit-entries endnu.</div>
        ) : (
          <ul className="divide-y divide-brand text-sm">
            {audit.map((a) => (
              <li key={a.id} className="p-3 flex flex-wrap justify-between gap-2">
                <span>
                  <strong>{a.action}</strong> · {a.actor}
                </span>
                <span className="text-brand-text2 text-xs">{a.at_iso?.slice(0, 19)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {deferredRuns.length > 0 && (
        <section className="bg-white border border-brand p-4 text-sm">
          <div className="text-xs uppercase tracking-widest text-brand-text2 mb-2">Seneste deferred cron</div>
          <ul className="space-y-1">
            {deferredRuns.map((r) => (
              <li key={r.id || r.ran_at_iso}>
                {r.ran_at_iso?.slice(0, 19)} — fundet {r.deferred_found ?? 0}, ryddet {r.cleared ?? 0}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
